import * as React from "react";
import i18n from "i18n";
import { Link } from "react-router-dom";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  withStyles,
} from "@material-ui/core";
import { FolderOpen } from "@material-ui/icons";
import ChildGroups from "modules/composition/components/layouts/ChildGroups";
import { CompositionStore } from "modules/composition/CompositionStore";
import "react-sticky-header/styles.css";
import { observer } from "mobx-react";
import { formatNumber } from "utils/MathUtils";

const styles = (theme) => ({
  container: {
    backgroundColor: "#f2f2f2",
    padding: "20px",
    height: "-webkit-fill-available",
    overflowY: "hidden" as any,
  },
  table: {
    marginTop: "20px",
  },
  header: {
    fontFamily: "Roboto",
    fontSize: "11px",
    fontWeight: 400,
    color: "#a1a1a1",
    backgroundColor: "unset",
    whiteSpace: "nowrap" as any,
  },
  group: {
    border: "0px",
    backgroundColor: "unset",
    height: "36px",
  },
  groupCell: {
    padding: "0px",
    fontFamily: "Roboto",
    fontSize: "12px",
    color: "#212121",
    fontWeight: 500,
  },
  groupIcon: {
    verticalAlign: "-webkit-baseline-middle",
  },
  folderIcon: {
    fontSize: "21px",
    marginRight: "15px",
    marginLeft: "10px",
  },
  linearProgress: {
    backgroundColor: "unset",
    color: "#ABC200",
    margin: "0 auto",
  },
  task: {
    backgroundColor: "#ffffff",
    height: "unset",
  },
  taskCell: {
    lineHeight: "17px",
    fontFamily: "Roboto",
    fontSize: "12px",
    color: "#5c5c5c",
    fontWeight: 400,
  },
  taskCellLoader: {
    height: "400px",
    verticalAlign: "middle",
    textAlign: "center" as any,
  },
  taskLink: {
    color: "#41A0FF",
    fontWeight: 700,
  },
  scroll: {
    marginTop: "20px",
    width: "100%",
    border: 0,
    borderCollapse: "collapse" as any,
    "& tr": {
      display: "flex",
    },
    "& td": {
      padding: "10px",
      flex: "2",
      width: "1px",
      "&:first-child": {
        flex: "1",
      },
    },
    "& thead tr:after": {
      content: "" as any,
      overflowY: "scroll" as any,
      visibility: "hidden" as any,
      height: 0,
    },
    "& thead th": {
      flex: "2",
      display: "block",
      padding: "4px 10px 4px 10px",
      "&:first-child": {
        flex: "1",
      },
    },
    "& tbody": {
      display: "block",
      width: "100%",
      overflowY: "auto" as any,
      height: "450px",
    },
  },
  groupCellFolder: {
    padding: "0 !important",
    fontFamily: "Roboto",
    fontSize: "12px",
    color: "#212121",
    fontWeight: 500,
  },
});

type IProps = {
  classes: any;
};

@observer
class GroupContent extends React.Component<IProps> {
  state = {
    loaded: false,
    pathString: "",
    hasAlternativeCode: false,
  };

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    if (location.pathname !== this.state.pathString) {
      this.state.pathString = location.pathname;
      this.getRateTasks();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (location.pathname !== this.state.pathString) {
      this.state.pathString = location.pathname;
      this.getRateTasks();
    }
  }

  getRateTasks = async () => {
    this.setState({ hasAlternativeCode: false });
    await CompositionStore.getRateTasksByGroup(CompositionStore.group.tasks);
    this.setState({ loaded: true });
    for (let i = 0; i < CompositionStore.rateTasks.length; i++) {
      const element = CompositionStore.rateTasks[i];
      if (
        element.data.alternativeCodeComposition ||
        element.data.alternativeCodeItem
      ) {
        this.setState({ hasAlternativeCode: true });
        break;
      }
    }
  };

  render() {
    const { classes } = this.props,
      { loaded, hasAlternativeCode } = this.state;

    // Função que pega a posição do scroll da tabela e passa para a variável compositionGroupScrollTop
    const getScroll = () => {
      const scroller = document.querySelector("#getScroll");
      if (scroller) {
        if (loaded && CompositionStore.loading === false) {
          scroller.addEventListener("scroll", (event: any) => {
            let locationArray = location.pathname.split("/");
            if (locationArray.length > 2) {
              CompositionStore.setCompositionGroupScrollTopObj(
                locationArray[locationArray.length - 1],
                scroller.scrollTop
              );
            }
          });
        }
      }
    };
    // Função que pega a posição do scroll salvo na variável compositionGroupScrollTop e seta na tabela
    const setScroll = () => {
      const scroller = document.querySelector("#getScroll");
      if (scroller) {
        if (loaded && CompositionStore.loading === false) {
          setTimeout(() => {
            let locationArray = location.pathname.split("/");
            if (locationArray.length > 2) {
              scroller.scrollTop =
                CompositionStore.getCompositionGroupScrollTopObj(
                  locationArray[locationArray.length - 1]
                );
              CompositionStore.setCompositionGroupScrollTopObj();
            }
          }, 100);
        }
      }
    };
    setScroll();
    return (
      <div className={classes.container}>
        <Table className={classes.scroll}>
          <TableHead className={classes.header}>
            <TableRow>
              <TableCell style={{ width: "110px" }}>
                {i18n.rateLibraries.header.code}
              </TableCell>
              {hasAlternativeCode && (
                <TableCell style={{ width: "100px" }}>
                  {i18n.rateLibraries.header.alternativeCode}
                </TableCell>
              )}
              <TableCell style={{ flex: 12 }}>
                {i18n.rateLibraries.header.description}
              </TableCell>
              <TableCell className={classes.taskCell} numeric>
                {i18n.rateLibraries.header.unity}
              </TableCell>
              <TableCell numeric>{i18n.rateLibraries.header.price}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody id="getScroll">
            <TableRow className={classes.group}>
              <TableCell colSpan={3} className={classes.groupCell}>
                <span className={classes.groupIcon}>
                  <FolderOpen className={classes.folderIcon} />
                </span>
                <span>
                  {CompositionStore.group.description.charAt(0).toUpperCase() +
                    CompositionStore.group.description.slice(1)}{" "}
                  ({CompositionStore.group.initial})
                </span>
              </TableCell>
            </TableRow>
            {loaded && CompositionStore.loading === false ? (
              (getScroll(),
              CompositionStore.rateTasks.map((item) => {
                let text = item.data.totalCost ? item.data.totalCost : 0.0;
                return (
                  <TableRow className={classes.task}>
                    <TableCell
                      className={classes.taskCell}
                      style={{ width: "110px" }}
                    >
                      {item.data.code}
                    </TableCell>
                    {hasAlternativeCode && (
                      <TableCell
                        className={classes.taskCell}
                        style={{ width: "100px" }}
                      >
                        {item.data.alternativeCodeComposition
                          ? item.data.alternativeCodeComposition
                          : item.data.alternativeCodeItem}
                      </TableCell>
                    )}
                    <TableCell
                      className={classes.taskCell}
                      style={{ flex: 12 }}
                    >
                      {item.data.description.charAt(0).toUpperCase() +
                        item.data.description.slice(1)}
                    </TableCell>
                    <TableCell className={classes.taskCell} numeric>
                      {item.data.unity}
                    </TableCell>
                    <TableCell className={classes.taskCell} numeric>
                      {item.data.type === "TASK" ? (
                        <Link
                          to={`${location.pathname}/task~${item.id}`}
                          className={classes.taskLink}
                          onClick={() => (CompositionStore.loading = false)}
                        >
                          {formatNumber(text)}
                        </Link>
                      ) : (
                        formatNumber(text)
                      )}
                    </TableCell>
                  </TableRow>
                );
              }))
            ) : (
              <TableRow className={classes.task}>
                <TableCell colSpan={3} className={classes.taskCellLoader}>
                  <CircularProgress
                    size={96}
                    className={classes.linearProgress}
                    variant="indeterminate"
                  />
                </TableCell>
              </TableRow>
            )}
            <ChildGroups />
          </TableBody>
        </Table>
      </div>
    );
  }
}

export default withStyles(styles)(GroupContent);



// WEBPACK FOOTER //
// ./src/modules/composition/components/layouts/GroupContent.tsx